/* eslint-disable no-plusplus,no-param-reassign */
/* global G */

/**
 * Builds the in-flight upload entry kept in the adapter's G.STATE for a single attachment.
 *
 * @param {string} parent       the key of the object (message, note, …) the attachment belongs to
 * @param {Object} attachment   the local attachment (group item) being uploaded
 * @returns {Object}            the upload entry
 */
const toEntry = (parent, attachment) => {
  const mimetype = attachment.value?.type || attachment.value?.mimetype
  return {
    parent,
    key: attachment.key,
    uuid: attachment.uuid || attachment.key,
    file: attachment.value,
    // Local preview url for images — reused for both the in-flight and the finished attachment (so
    // the image shows right away, without a reload/flash) and revoked on cancel.
    url: (mimetype || '').startsWith('image') ? URL.createObjectURL(attachment.value) : null,
    name: attachment.value?.name,
    size: attachment.value?.size,
    mimetype,
    progress: 0,
    status: 'uploading',
    id: null,
    rev: null,
  }
}

/**
 * Removes all of `parent`'s entries from the `uploads` list.
 *
 * @param {Object[]} uploads   the adapter's G.STATE uploads list
 * @param {string} parent      the parent key whose entries should be removed
 */
const removeParentEntries = (uploads, parent) => {
  for (let i = uploads.length - 1; i >= 0; i--) {
    uploads[i].parent === parent && uploads.splice(i, 1)
  }
}

/**
 * Handles in-flight attachments (currently being uploaded).
 *
 * Exposes them via `G.ATTACHMENT, G.PROGRESS`, attaches a handler to each
 * attachment that will clean up properly after the upload is finished.
 *
 * @param {Gaia.Web.Application} app  the Platform Web Application
 */
const fn = app => ({ detail }) => {
  const adapter = app[G.ADAPTER][G.ATTACHMENT]
  const eventBus = app[G.ADAPTER][G.EVENTS]
  const { parent, attachments = [] } = detail[G.DATA] || {}

  if (!parent || !attachments.length) return

  const { uploads } = adapter[G.STATE]

  const entries = attachments.map(attachment => toEntry(parent, attachment))
  uploads.push(...entries)
  eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.PROGRESS), { [G.DATA]: uploads })

  entries.forEach((entry) => {
    const eventType = eventBus.type(G.ATTACHMENT, G.READ, entry.key)
    const handler = ({ detail: read }) => {
      if (read.key !== entry.key) return

      if (read.status === 201) {
        entry.progress = 100
        entry.status = 'done'
        entry.id = read.response?.[0]?.id ?? null
        entry.rev = read.response?.[0]?.rev ?? null
        eventBus.remove(eventType, handler)
      } else if (read.status >= 400) {
        entry.status = 'error'
        eventBus.remove(eventType, handler)
      } else {
        entry.progress = read.progress ?? entry.progress
      }

      const pending = uploads.filter(e => e.parent === parent && e.status === 'uploading')

      // all uploaded
      if (!pending.length) {
        const completed = uploads
          .filter(e => e.parent === parent && e.status === 'done')
          .map(e => ({
            key: e.id,
            _rev: e.rev,
            ...e.url && { file: e.url },
            value: { name: e.name, size: e.size, mimetype: e.mimetype },
          }))

        removeParentEntries(uploads, parent)
        eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.DONE, parent), { [G.DATA]: completed })
      }

      eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.PROGRESS), { [G.DATA]: uploads })
    }

    // Kept on the entry so a cancel can detach this listener again.
    entry.readEventType = eventType
    entry.readHandler = handler
    eventBus.add(eventType, handler)
  })
}

export default fn
